import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import API from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Globe, Search, Loader2, ExternalLink, Import, FolderKanban, ShoppingCart } from 'lucide-react';
import { toast } from 'sonner';

export default function SeapSearchPage() {
  const { user } = useAuth();
  const isAutoritate = user?.user_type === 'autoritate';
  const [query, setQuery] = useState('');
  const [cpv, setCpv] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const navigate = useNavigate();

  const handleSearch = async () => {
    if (!query.trim() && !cpv.trim()) { toast.error('Introdu un termen de cautare'); return; }
    setLoading(true);
    setSearched(true);
    try {
      const endpoint = isAutoritate ? '/seap/search-direct' : '/seap/search';
      const res = await API.get(endpoint, { params: { query, cpv } });
      setResults(res.data.results || []);
      if (res.data.results?.length === 0) toast.info('Niciun rezultat gasit');
    } catch { toast.error('Eroare la cautare'); }
    finally { setLoading(false); }
  };

  const handleImport = async (result) => {
    try {
      const moduleType = isAutoritate ? 'autoritate' : user?.user_type || 'ofertant';
      const procType = isAutoritate ? 'achizitie_directa' : 'licitatie_deschisa';
      const res = await API.post('/seap/import', { title: result.title, url: result.url, module_type: moduleType, procurement_type: procType });
      toast.success(isAutoritate ? 'Achizitie directa importata!' : 'Licitatie importata ca proiect!');
      navigate(`/projects/${res.data.id}`);
    } catch { toast.error('Eroare la import'); }
  };

  const quickTags = isAutoritate
    ? ['emulsie bituminoasa', 'agregate', 'mixtura asfaltica', 'ciment', 'marcaje rutiere', 'inchiriere utilaje']
    : ['lucrari de drumuri', 'asfaltare', 'reparare drumuri', 'reasfaltare', 'marcaje rutiere', 'intretinere drumuri', 'pavare', 'trotuare'];

  return (
    <div className="p-8" data-testid="seap-search-page">
      <div className="mb-8">
        <h1 className="text-2xl font-bold flex items-center gap-3" style={{ fontFamily: 'Outfit' }}>
          {isAutoritate ? <ShoppingCart className="w-6 h-6 text-emerald-400" strokeWidth={1.5} /> : <Globe className="w-6 h-6 text-primary" strokeWidth={1.5} />}
          {isAutoritate ? 'Achizitii Directe SICAP' : 'Cautare SEAP'}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          {isAutoritate
            ? 'Cautare produse si servicii in catalogul electronic SICAP pentru achizitii directe'
            : 'Cauta licitatii active pe portalul e-licitatie.ro si importa-le ca proiecte'}
        </p>
        {isAutoritate && (
          <div className="flex items-center gap-2 mt-2">
            <Badge variant="outline" className="text-[10px] bg-emerald-500/10 text-emerald-400 ring-emerald-500/20 ring-1 ring-inset border-0">
              Praguri achizitii directe
            </Badge>
            <span className="text-xs text-muted-foreground">Produse/Servicii: sub 270.120 RON | Lucrari: sub 900.400 RON</span>
          </div>
        )}
      </div>

      <Card className="bg-card border-white/10 mb-6">
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
            <div className="md:col-span-1">
              <Label>{isAutoritate ? 'Produs / Serviciu' : 'Cuvinte cheie'}</Label>
              <Input value={query} onChange={e => setQuery(e.target.value)}
                placeholder={isAutoritate ? 'Ex: emulsie bituminoasa, agregate, mixtura asfaltica...' : 'Ex: asfaltare, lucrari de drumuri, marcaje rutiere...'}
                className="mt-1.5 bg-zinc-900/50 border-white/10" data-testid="seap-query"
                onKeyDown={e => e.key === 'Enter' && handleSearch()} />
            </div>
            <div>
              <Label>Cod CPV (optional)</Label>
              <Input value={cpv} onChange={e => setCpv(e.target.value)} placeholder="Ex: 45233120 (constructii drumuri)"
                className="mt-1.5 bg-zinc-900/50 border-white/10 font-mono-data" data-testid="seap-cpv" />
            </div>
            <Button onClick={handleSearch} disabled={loading} className="h-10" data-testid="seap-search-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
              {loading ? 'Se cauta...' : isAutoritate ? 'Cauta in Catalog' : 'Cauta pe SEAP'}
            </Button>
          </div>
          <div className="flex flex-wrap gap-2 mt-4">
            {quickTags.map(tag => (
              <button key={tag} onClick={() => setQuery(tag)}
                className="text-xs px-2.5 py-1 rounded-full bg-zinc-800 text-zinc-400 hover:bg-primary/10 hover:text-primary transition-colors">
                {tag}
              </button>
            ))}
          </div>
          {isAutoritate && (
            <div className="mt-4 p-3 rounded-lg bg-zinc-900/50 border border-white/5">
              <p className="text-xs text-muted-foreground">
                <span className="text-amber-400 font-medium">Sursa date:</span> e-licitatie.ro/pub/direct-acquisitions - Catalogul electronic de produse SICAP
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {results.length > 0 ? (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground mb-2">{results.length} rezultate gasite</p>
          {results.map((r, i) => (
            <Card key={i} className="bg-card border-white/10 hover:border-white/20 transition-all" data-testid={`seap-result-${i}`}>
              <CardContent className="p-5">
                <div className="flex items-start gap-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-medium mb-1 line-clamp-2">{r.title}</h3>
                    {r.authority && <p className="text-[11px] text-muted-foreground mb-1 truncate">{r.authority}</p>}
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className="text-[10px] bg-emerald-500/10 text-emerald-400 ring-emerald-500/20 ring-1 ring-inset border-0">
                        {r.source || (isAutoritate ? 'SICAP Catalog' : 'SEAP')}
                      </Badge>
                      {r.is_biddable === true && <Badge className="text-[10px] bg-emerald-500 text-white">Deschisa</Badge>}
                      {r.is_biddable === false && <Badge className="text-[10px] bg-zinc-600 text-white">Expirata</Badge>}
                      {r.deadline && <span className="text-xs text-amber-400 font-mono-data">Termen: {String(r.deadline).slice(0, 10)}</span>}
                      {r.value ? <span className="text-xs text-muted-foreground font-mono-data">{Number(r.value).toLocaleString('ro-RO')} RON</span> : null}
                      {r.cpv && <span className="text-xs text-muted-foreground font-mono-data truncate max-w-[240px]">{r.cpv}</span>}
                      {r.url && (
                        <a href={r.url.startsWith('http') ? r.url : `https://e-licitatie.ro${r.url}`}
                          target="_blank" rel="noopener noreferrer"
                          className="text-xs text-primary hover:underline flex items-center gap-1">
                          Deschide <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => handleImport(r)} data-testid={`seap-import-${i}`} className="shrink-0 text-xs">
                    <Import className="w-3.5 h-3.5 mr-1.5" />{isAutoritate ? 'Initieaza' : 'Importa'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : searched && !loading ? (
        <Card className="bg-card border-white/10 border-dashed">
          <CardContent className="py-12 text-center">
            {isAutoritate ? <ShoppingCart className="w-12 h-12 mx-auto text-muted-foreground/20 mb-4" strokeWidth={1.5} /> : <Globe className="w-12 h-12 mx-auto text-muted-foreground/20 mb-4" strokeWidth={1.5} />}
            <p className="text-muted-foreground text-sm mb-1">Niciun rezultat gasit</p>
            <p className="text-xs text-muted-foreground">
              {isAutoritate ? 'Verificati catalogul SICAP direct pe e-licitatie.ro' : 'Incercati alti termeni sau verificati SEAP direct'}
            </p>
          </CardContent>
        </Card>
      ) : !searched ? (
        <Card className="bg-card border-white/10 border-dashed">
          <CardContent className="py-12 text-center">
            <FolderKanban className="w-12 h-12 mx-auto text-muted-foreground/20 mb-4" strokeWidth={1.5} />
            <p className="text-muted-foreground text-sm mb-1">
              {isAutoritate ? 'Cautati produse/servicii in catalogul SICAP' : 'Cautati licitatii pe SEAP/SICAP'}
            </p>
            <p className="text-xs text-muted-foreground">Introduceti cuvinte cheie sau cod CPV si apasati "Cauta"</p>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
